import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PostmasterV2ComplianceTab from './PostmasterV2ComplianceTab';
import PostmasterV2DeliveryErrorsTab from './PostmasterV2DeliveryErrorsTab';
import PostmasterV2FblTab from './PostmasterV2FblTab';
import {
  ExpandMore,
  HelpOutline,
  InfoOutlined,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { ResponsiveLine, Serie } from '@nivo/line';
import moment from 'moment';
import Header from '../../components/Header';
import { FilterContext } from '../../contexts/FilterContext';
import { api } from '../../helpers/apiUtils';

interface PostmasterDomain {
  name: string;
  domain: string;
  permission?: string | null;
}

interface IpReputationBucket {
  reputation: 'BAD' | 'LOW' | 'MEDIUM' | 'HIGH';
  ipCount?: string | number;
  sampleIps?: string[];
}

interface PostmasterStatRow {
  domain: string;
  stat_date: string;
  user_reported_spam_ratio: number | null;
  user_reported_spam_ratio_lower_bound: number | null;
  user_reported_spam_ratio_upper_bound: number | null;
  spf_success_ratio: number | null;
  dkim_success_ratio: number | null;
  dmarc_success_ratio: number | null;
  inbound_encryption_ratio: number | null;
  outbound_encryption_ratio: number | null;
  domain_reputation: string | null;
  ip_reputations: IpReputationBucket[] | null;
  delivery_errors: unknown;
}

const pct = (v: number | null): string =>
  v === null || v === undefined ? '–' : `${(v * 100).toFixed(2)} %`;

// Spam-rate coloring: Google flags > 0.3% as problematic, >= 0.1% worth watching
const spamColor = (v: number | null): string => {
  if (v === null || v === undefined) return 'text.secondary';
  if (v >= 0.003) return 'error.main';
  if (v >= 0.001) return 'warning.main';
  return 'success.main';
};

const authColor = (v: number | null): string => {
  if (v === null || v === undefined) return 'text.secondary';
  if (v >= 0.98) return 'success.main';
  if (v >= 0.9) return 'warning.main';
  return 'error.main';
};

// V2's AUTH_SUCCESS_RATE reports 0% on the From-header domain when the SPF
// check actually ran against a different Return-Path domain (e.g. news.* sends
// via SparkPost where Return-Path = bounces.finanztrends.de — Gmail headers
// confirm spf=pass on bounces.*, not news.*). V1 returned NULL in that case;
// V2's denominator convention reports literal 0, which the dashboard previously
// painted red despite Gmail authenticating the mail correctly.
//
// Detection: SPF=0 with DKIM=100% AND DMARC=100% is the smoking-gun pattern
// for "SPF wasn't evaluated on this domain" — if SPF were really failing
// across the board, real-world Gmail authentication wouldn't survive at that
// rate. Limited to SPF only — DKIM/DMARC on these domains genuinely pass.
// Heuristic is rendering-only; raw V2 value is preserved in postmaster_domain_stats
// for audit + the bounces.* row remains the authoritative SPF metric.
const isLikelyNoSpfEval = (r: PostmasterStatRow): boolean =>
  r.spf_success_ratio === 0 && r.dkim_success_ratio === 1 && r.dmarc_success_ratio === 1;

const displaySpf = (r: PostmasterStatRow): number | null =>
  isLikelyNoSpfEval(r) ? null : r.spf_success_ratio;

// Domain reputation ordinal scale (BAD=1, LOW=2, MEDIUM=3, HIGH=4).
// Google does not publish "none-reputation" so missing values stay NULL.
const REPUTATION_SCORE: Record<string, number> = {
  BAD: 1,
  LOW: 2,
  MEDIUM: 3,
  HIGH: 4,
};
const REPUTATION_LABEL: Record<number, string> = {
  1: 'BAD',
  2: 'LOW',
  3: 'MEDIUM',
  4: 'HIGH',
};
const reputationColor = (rep: string | null): string => {
  if (!rep) return 'text.secondary';
  if (rep === 'HIGH') return 'success.main';
  if (rep === 'MEDIUM') return 'info.main';
  if (rep === 'LOW') return 'warning.main';
  return 'error.main';
};

// Identify sending-infrastructure provider from known IP ranges.
// Based on reverse-DNS lookups done 2026-04-20; PTR format indicates shared pool.
// Shared-pool PTR examples:
//   156.70.34.96  → mta-70-34-96.sparkpostmail.com
//   198.2.141.72  → mail72.atl231.mcsv.net
// Dedicated IPs would have customer-specific rDNS like mta.news.finanztrends.de.
function classifyIp(ip: string): { provider: string; pool: string } | null {
  // SparkPost EU (Bird.com) — used by Iterable campaigns
  // (From: news.finanztrends.de). Confirmed via Feedback-ID:iterable header.
  // Pool-Typ (shared vs dedicated) ist extern nicht verifizierbar —
  // bei Iterable-Support anfragen.
  if (
    ip.startsWith('156.70.34.') ||
    ip.startsWith('156.70.35.') ||
    /^168\.203\.(3[2-9]|4\d|5\d|6[0-3])\./.test(ip)
  ) {
    return { provider: 'SparkPost EU', pool: 'Iterable → SparkPost' };
  }
  // Mailchimp (Atlanta) — RSG-DELIVERY netblock 198.2.128.0/18
  // Used only as backup + webinar reminders.
  if (/^198\.2\.(12[89]|1[3-9]\d|1[8-9][0-1])\./.test(ip)) {
    return { provider: 'Mailchimp', pool: 'Backup + Webinar reminders' };
  }
  // ispgateway.de / DomainFactory — LPs opt-ins + dashboard daily reports
  if (ip.startsWith('80.67.') || ip.startsWith('134.119.')) {
    return { provider: 'ispgateway.de', pool: 'LPs + Dashboard SMTP' };
  }
  return null;
}

// Centralized metric tooltips — German, plain language, no ESP jargon.
const TOOLTIPS = {
  spam: "Anteil der Gmail-Nutzer, die Ihre Mail über den 'Spam melden'-Button markiert haben. Google stuft alles über 0,3 % als problematisch ein. Nur Mails an authentifizierte Domains werden gezählt.",
  spf: 'Prozentsatz der Mails, bei denen die SPF-Prüfung bestanden hat. NULL bedeutet: SPF wird über eine andere Bounce-Domain (z. B. bounces.finanztrends.de) geprüft — kein Fehler.',
  dkim: 'Prozentsatz der Mails mit gültiger DKIM-Signatur, die auf Ihre Domain ausgerichtet ist. Sollte dauerhaft bei 100 % liegen.',
  dmarc:
    'Prozentsatz der Mails, die DMARC-Prüfung bestehen (SPF oder DKIM müssen zur sichtbaren Absenderdomain passen). 100 % ist der Zielwert.',
  reputation:
    'Gmails zusammenfassendes Vertrauensurteil pro Domain: HIGH = Inbox, MEDIUM = meist Inbox, LOW = oft Spam, BAD = fast immer Spam. Basiert auf Nutzerverhalten, Spam-Raten und Authentifizierung.',
  deliveryErrors:
    'Von Gmail zurückgewiesene Zustellversuche an diesem Tag — z. B. SUSPECTED_SPAM oder RATE_LIMITED. Zeile ausklappen für Details.',
  ipReputations:
    'Reputation der einzelnen Versand-IPs, wie Gmail sie sieht. Ihre Domain-Reputation kann nie besser sein als die schlechtesten IPs. BAD/LOW IPs ziehen die Domain-Reputation runter.',
  inboundEnc: 'Anteil der bei Gmail eingehenden Mails, die TLS-verschlüsselt übertragen wurden.',
  outboundEnc:
    'Anteil der von Gmail ausgehenden Mails an Ihre Domain, die TLS-verschlüsselt übertragen wurden.',
};

const MetricLabel: React.FC<{ label: string; tooltip: string }> = ({ label, tooltip }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    {label}
    <Tooltip title={tooltip} arrow>
      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary' }} />
    </Tooltip>
  </Box>
);

// Domains hidden by default in the central selector. neopresse.com is a
// separate publisher tracked here for completeness but rarely the focus of
// finanztrends-side deliverability work; finanztrends-newsletter.de is a
// foreign domain we only have READER access on.
const DEFAULT_HIDDEN_DOMAINS = new Set(['neopresse.com', 'finanztrends-newsletter.de']);

const PostmasterDashboard: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const [domains, setDomains] = useState<PostmasterDomain[]>([]);
  // null = central filter not yet decided (initial load). Child sections
  // fall back to "show all rows they fetched" while null.
  const [selectedDomains, setSelectedDomains] = useState<string[] | null>(null);
  const [rows, setRows] = useState<PostmasterStatRow[]>([]);
  const [loadingDomains, setLoadingDomains] = useState<boolean>(true);
  const [loadingStats, setLoadingStats] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // null = "show all IPs" (default). Once user interacts, becomes an array.
  const [selectedIps, setSelectedIps] = useState<string[] | null>(null);

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Load domain list once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingDomains(true);
        const res = await api.get('/api/postmaster/domains');
        if (cancelled) return;
        const list: PostmasterDomain[] = res.data.data || [];
        setDomains(list);
        // Default selection: every registered domain EXCEPT the hidden-by-default
        // set. Users can re-add neopresse / newsletter.* via the central selector
        // when they need cross-domain comparisons.
        const defaultSelection = list
          .map((d) => d.domain)
          .filter((d) => !DEFAULT_HIDDEN_DOMAINS.has(d));
        setSelectedDomains(
          defaultSelection.length > 0 ? defaultSelection : list.map((d) => d.domain),
        );
      } catch (err) {
        if (!cancelled) setError(`Konnte Domains nicht laden: ${(err as Error).message}`);
      } finally {
        if (!cancelled) setLoadingDomains(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load stats when date range or selected domains change
  useEffect(() => {
    if (
      !filterState.startDate ||
      !filterState.endDate ||
      selectedDomains === null ||
      selectedDomains.length === 0
    ) {
      setRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingStats(true);
        setError(null);
        const res = await api.get('/api/postmaster/stats', {
          params: {
            startDate: moment(filterState.startDate).format('YYYY-MM-DD'),
            endDate: moment(filterState.endDate).format('YYYY-MM-DD'),
            domain: selectedDomains.join(','),
          },
        });
        if (!cancelled) setRows(res.data.data || []);
      } catch (err) {
        if (!cancelled) setError(`Konnte Daten nicht laden: ${(err as Error).message}`);
      } finally {
        if (!cancelled) setLoadingStats(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterState.startDate, filterState.endDate, selectedDomains]);

  // Aggregate latest-day KPIs per domain
  const latestByDomain = useMemo(() => {
    const map = new Map<string, PostmasterStatRow>();
    for (const r of rows) {
      const existing = map.get(r.domain);
      if (!existing || r.stat_date > existing.stat_date) map.set(r.domain, r);
    }
    return Array.from(map.values()).sort((a, b) => a.domain.localeCompare(b.domain));
  }, [rows]);

  // Line chart series: spam rate per domain
  const spamSeries: Serie[] = useMemo(() => {
    const byDomain = new Map<string, { x: string; y: number }[]>();
    for (const r of rows) {
      if (r.user_reported_spam_ratio === null) continue;
      if (!byDomain.has(r.domain)) byDomain.set(r.domain, []);
      byDomain.get(r.domain)!.push({ x: r.stat_date, y: r.user_reported_spam_ratio * 100 });
    }
    return Array.from(byDomain.entries()).map(([domain, data]) => ({
      id: domain,
      data,
    }));
  }, [rows]);

  // Y-axis max for spam chart: at least 0.4 % so Google's 0.3 % line is
  // always visible even on low-rate days; expand to max(data) + headroom above.
  const spamYMax = useMemo(() => {
    let max = 0;
    for (const s of spamSeries) {
      for (const d of s.data) {
        const y = typeof d.y === 'number' ? d.y : 0;
        if (y > max) max = y;
      }
    }
    return Math.max(0.4, max * 1.15);
  }, [spamSeries]);

  // Latest-day IP reputations per domain: flatten buckets to per-IP rows
  const latestIpReputations = useMemo(() => {
    // Use the most recent day per domain
    const byDomain = new Map<string, { stat_date: string; buckets: IpReputationBucket[] }>();
    for (const r of rows) {
      if (!r.ip_reputations) continue;
      const prev = byDomain.get(r.domain);
      if (!prev || r.stat_date > prev.stat_date) {
        byDomain.set(r.domain, {
          stat_date: r.stat_date,
          buckets: r.ip_reputations,
        });
      }
    }
    return Array.from(byDomain.entries())
      .map(([domain, { stat_date, buckets }]) => ({
        domain,
        stat_date,
        buckets,
      }))
      .sort((a, b) => a.domain.localeCompare(b.domain));
  }, [rows]);

  // Flat list of {date, domain, ip, reputation} tuples, one per (day × IP)
  const ipDaily = useMemo(() => {
    const out: Array<{
      stat_date: string;
      domain: string;
      ip: string;
      reputation: string;
    }> = [];
    for (const r of rows) {
      if (!r.ip_reputations) continue;
      for (const bucket of r.ip_reputations) {
        const ips = bucket.sampleIps ?? [];
        for (const ip of ips) {
          out.push({
            stat_date: r.stat_date,
            domain: r.domain,
            ip,
            reputation: bucket.reputation,
          });
        }
      }
    }
    return out;
  }, [rows]);

  // All IPs ever seen across the window, for the multi-select options
  const allIps = useMemo(() => {
    const set = new Set<string>();
    for (const d of ipDaily) set.add(d.ip);
    return Array.from(set).sort();
  }, [ipDaily]);

  // Effective selection: null means "show all"
  const effectiveSelectedIps: string[] = selectedIps ?? allIps;

  // IP reputation time-series: one series per selected IP.
  // If the same IP sends from multiple domains on the same day, take the worst
  // reputation (min score) — that's the one dragging domain reputation down.
  const ipReputationSeries: Serie[] = useMemo(() => {
    const byIpDate = new Map<string, Map<string, number>>();
    const selected = selectedIps ?? allIps;
    for (const d of ipDaily) {
      if (!selected.includes(d.ip)) continue;
      const score = REPUTATION_SCORE[d.reputation];
      if (!score) continue;
      if (!byIpDate.has(d.ip)) byIpDate.set(d.ip, new Map());
      const dateMap = byIpDate.get(d.ip)!;
      const existing = dateMap.get(d.stat_date);
      // Keep the minimum (worst) score if IP appears across multiple domains
      dateMap.set(d.stat_date, existing !== undefined ? Math.min(existing, score) : score);
    }
    return Array.from(byIpDate.entries()).map(([ip, dateMap]) => ({
      id: ip,
      data: Array.from(dateMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, score]) => ({ x: date, y: score })),
    }));
  }, [ipDaily, selectedIps, allIps]);

  // Reputation chart series: per domain, mapped to ordinal 1-4 scale
  const reputationSeries: Serie[] = useMemo(() => {
    const byDomain = new Map<string, { x: string; y: number; raw: string }[]>();
    for (const r of rows) {
      if (!r.domain_reputation || !REPUTATION_SCORE[r.domain_reputation]) continue;
      if (!byDomain.has(r.domain)) byDomain.set(r.domain, []);
      byDomain.get(r.domain)!.push({
        x: r.stat_date,
        y: REPUTATION_SCORE[r.domain_reputation],
        raw: r.domain_reputation,
      });
    }
    return Array.from(byDomain.entries()).map(([domain, data]) => ({
      id: domain,
      data: data.sort((a, b) => a.x.localeCompare(b.x)),
    }));
  }, [rows]);

  return (
    <Box m="20px">
      <Header
        title="Email Deliverability"
        subtitle="Google Postmaster Tools — Gmail-spezifische Zustellbarkeit"
      />

      {/* Central domain filter — applies to all three sections below.
          neopresse.com + finanztrends-newsletter.de are pre-deselected; users
          can re-add them when they need cross-publisher comparisons. */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Autocomplete
          multiple
          options={domains.map((d) => d.domain)}
          value={selectedDomains ?? []}
          onChange={(_, value) => setSelectedDomains(value)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const tagProps = getTagProps({ index });
              return <Chip label={option} {...tagProps} key={tagProps.key} />;
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Domains (global filter)"
              placeholder="Domain auswählen"
              helperText="Filtert Compliance, Traffic & Reputation und FBL gleichzeitig."
            />
          )}
          disabled={loadingDomains}
        />
      </Paper>

      {/* Section 1 — Compliance Status (V2 ComplianceStatus per domain) */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
          Compliance Status
        </Typography>
        <PostmasterV2ComplianceTab selectedDomains={selectedDomains} />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Section 2 — Traffic & Reputation (postmaster_domain_stats; V1 columns frozen) */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
          Traffic & Reputation
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Domain- und IP-Reputation von Google eingestellt (27.08.2026):</strong> Mit der
          Abschaltung der Postmaster-API v1 hat Google die 4-stufige Domain-Reputation und die
          Per-IP-Reputation ersatzlos gestrichen — die Charts unten zeigen die eingefrorene
          Historie (letzter Datenpunkt 23.08.2026). Aktuelle Signale: das Zustellbarkeits-Verdict
          in der Compliance-Sektion und die Ablehnungsgründe weiter unten. Spam-Rate, SPF/DKIM/
          DMARC und TLS laufen normal weiter (V2-Daten).
        </Alert>
        {/* Setup context — collapsed by default */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoOutlined sx={{ fontSize: 18, color: 'info.main' }} />
              <Typography variant="subtitle2">
                Hinweise zum Iterable-/SparkPost-Setup (SPF, Return-Path, Reputation)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" gutterBottom>
              <strong>Versand-Infrastruktur (3 Pfade):</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 1, '& li': { mb: 0.5 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Iterable → SparkPost EU</strong> — alle Newsletter-, Webinar- und
                  PLX-Mailing-Kampagnen. From: <code>news.finanztrends.de</code>, Return-Path{' '}
                  <code>bounces.finanztrends.de</code> (CNAME → <code>eu.sparkpostmail.com</code>
                  ). IPs aktuell: <code>156.70.34.96</code>, <code>168.203.43.234</code>.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Mailchimp (Backup)</strong> — nur für Backup-Sends + Webinar-Reminder.
                  IPs: <code>198.2.x.x</code> (RSG-DELIVERY Atlanta).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>LPs + Dashboard-SMTP</strong> — Opt-in-Bestätigungen (LPs, hostet unter{' '}
                  <code>lps.finanztrends.info</code>) sowie Daily-Reports aus dem Dashboard (1-10
                  Mails/Tag, z. B. SoldLeads/Proffe-Report). Gehen über{' '}
                  <code>smtprelaypool.ispgateway.de</code> → <code>80.67.29.x</code>{' '}
                  (DomainFactory). From: <code>finanztrends.info</code> bzw.{' '}
                  <code>info@finanztrends.de</code>. Volume &lt; 100/Tag an Gmail — erscheint{' '}
                  <strong>nicht</strong> in Postmaster.
                </Typography>
              </li>
            </Box>

            <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
              <strong>
                Warum finanztrends.de dieselben SparkPost-IPs zeigt wie news.finanztrends.de:
              </strong>{' '}
              Google Postmaster aggregiert Mail-Aktivität über die Organisations-Domain
              (DMARC-Org-Domain-Gruppierung). Weil <code>news.finanztrends.de</code> und{' '}
              <code>finanztrends.de</code> dieselbe Org-Domain (<code>finanztrends.de</code>)
              teilen, tauchen die Iterable/SparkPost-IPs in beiden Profilen auf — obwohl
              finanztrends.de selbst nur die ispgateway-Mails mit &lt; 100/Tag versendet (unter
              Postmaster-Schwelle). Es sendet also nicht tatsächlich finanztrends.de von den
              SparkPost-IPs.
            </Typography>

            <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
              <strong>SPF-Prüfung (wichtig):</strong> Gmail prüft SPF immer gegen den Return-Path,
              nie gegen das sichtbare From. Das SPF-Feld in dieser Tabelle ist für{' '}
              <code>news.finanztrends.de</code> deshalb immer <em>leer</em> — das ist kein Fehler,
              sondern architekturbedingt. Die tatsächliche SPF-Prüfung läuft über{' '}
              <code>bounces.finanztrends.de</code> und bestanden (Header: <code>spf=pass</code>).
              Die dortige Domain ist jetzt ebenfalls in Postmaster Tools registriert — Daten
              erscheinen in ~2–3 Tagen.
            </Typography>
            <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
              <strong>Wo Sie SPF wirklich ablesen:</strong> V2 der Postmaster-API liefert bei dieser
              Architektur literal <code>0%</code> auf dem From-Domain (V1 hatte NULL geliefert).
              Wenn Sie also SPF = 0% mit DKIM = 100% und DMARC = 100% sehen, wurde SPF auf dieser
              Domain gar nicht evaluiert — Sie sehen die echte Rate in der Zeile{' '}
              <code>bounces.finanztrends.de</code>. Das Dashboard erkennt diese Konstellation und
              rendert SPF daher als <code>–</code> statt 0%.
            </Typography>

            <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
              <strong>DKIM / DMARC:</strong> DKIM wird auf <code>news.finanztrends.de</code>{' '}
              signiert (Selektor <code>scph0425</code>) und richtet sich über DMARC relaxed mit dem
              From-Header aus. Deshalb zeigen DKIM und DMARC hier dauerhaft 100 %.
            </Typography>

            <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
              <strong>Historie:</strong> Die im September 2025 korrigierte SPF-Syntax (
              <code>include:_spf.eu.sparkpostmail.com</code> mit Unterstrich) war ein kosmetischer
              Fix für einen mxtoolbox-Falschalarm. Funktional war SPF davor wie danach identisch —
              weil die tatsächliche Prüfung über den Return-Path läuft, nie über den{' '}
              <code>news.*</code>
              -SPF-Eintrag.
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>LOW-Reputation ist nicht Auth-bedingt:</strong> Authentifizierung ist grün.
              Die LOW-Einstufung kommt von (a) Spam-Report-Rate um 0,3 % über dem
              Google-Schwellenwert und (b) den zwei SparkPost-EU IPs <code>156.70.34.96</code>,{' '}
              <code>168.203.43.234</code> mit LOW-Reputation. Behebung: Engagement-Hygiene (inaktive
              User raus). Ob dedizierte IPs helfen, hängt vom Pool-Typ ab — siehe nächster Punkt.
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>IP-Pool-Typ (shared vs. dediziert) nicht extern verifizierbar:</strong> Das
              rDNS-Muster (<code>mta-70-34-96.sparkpostmail.com</code>) ist SparkPosts Standard für
              generische Pools, spricht also tendenziell für shared. Das ist aber keine Bestätigung
              — dedizierte IPs können dasselbe rDNS behalten, wenn kein Custom-PTR angefragt wurde.
              Nur Iterable/SparkPost-Support kann verbindlich antworten (siehe Support-Mail-Vorlage
              in der Session-Doku).
            </Typography>
          </AccordionDetails>
        </Accordion>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light' }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        {/* KPI cards — latest day per domain */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {latestByDomain.map((r) => (
            <Grid item xs={12} md={6} lg={4} key={r.domain}>
              <Card>
                <CardContent>
                  <Typography variant="h6">{r.domain}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Letzter Datenpunkt: {r.stat_date}
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="body2">
                      <MetricLabel label="Spam-Rate" tooltip={TOOLTIPS.spam} />:{' '}
                      <Box
                        component="span"
                        sx={{
                          color: spamColor(r.user_reported_spam_ratio),
                          fontWeight: 600,
                        }}
                      >
                        {pct(r.user_reported_spam_ratio)}
                      </Box>
                    </Typography>
                    <Typography variant="body2">
                      <MetricLabel label="SPF" tooltip={TOOLTIPS.spf} />:{' '}
                      <Box
                        component="span"
                        sx={{
                          color: authColor(displaySpf(r)),
                          fontWeight: 600,
                        }}
                      >
                        {pct(displaySpf(r))}
                      </Box>
                    </Typography>
                    <Typography variant="body2">
                      <MetricLabel label="DKIM" tooltip={TOOLTIPS.dkim} />:{' '}
                      <Box
                        component="span"
                        sx={{
                          color: authColor(r.dkim_success_ratio),
                          fontWeight: 600,
                        }}
                      >
                        {pct(r.dkim_success_ratio)}
                      </Box>
                    </Typography>
                    <Typography variant="body2">
                      <MetricLabel label="DMARC" tooltip={TOOLTIPS.dmarc} />:{' '}
                      <Box
                        component="span"
                        sx={{
                          color: authColor(r.dmarc_success_ratio),
                          fontWeight: 600,
                        }}
                      >
                        {pct(r.dmarc_success_ratio)}
                      </Box>
                    </Typography>
                    {r.domain_reputation && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <MetricLabel label="Domain-Reputation" tooltip={TOOLTIPS.reputation} />:{' '}
                        <Box
                          component="span"
                          sx={{
                            color: reputationColor(r.domain_reputation),
                            fontWeight: 600,
                          }}
                        >
                          {r.domain_reputation}
                        </Box>
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Spam rate chart */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography variant="h6">Spam-Rate pro Domain</Typography>
            <Tooltip title={TOOLTIPS.spam} arrow>
              <HelpOutline sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
          </Box>
          {loadingStats ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 350 }}>
              <ResponsiveLine
                data={spamSeries}
                margin={{ top: 20, right: 180, bottom: 70, left: 70 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0, max: spamYMax }}
                axisBottom={{
                  tickRotation: -45,
                  legend: 'Datum',
                  legendPosition: 'middle',
                  legendOffset: 55,
                }}
                axisLeft={{
                  format: (v) => `${(v as number).toFixed(2)} %`,
                  legend: 'Spam-Rate (%)',
                  legendOffset: -55,
                  legendPosition: 'middle',
                }}
                markers={[
                  {
                    axis: 'y',
                    value: 0.1,
                    legend: 'Aufmerksamkeit (0,1 %)',
                    legendOrientation: 'horizontal',
                    legendPosition: 'top-left',
                    textStyle: { fill: '#d97706', fontSize: 10 },
                    lineStyle: {
                      stroke: '#d97706',
                      strokeWidth: 1,
                      strokeDasharray: '4 4',
                    },
                  },
                  {
                    axis: 'y',
                    value: 0.3,
                    legend: 'Google-Schwellenwert (0,3 %)',
                    legendOrientation: 'horizontal',
                    legendPosition: 'top-left',
                    textStyle: { fill: '#dc2626', fontSize: 10, fontWeight: 600 },
                    lineStyle: {
                      stroke: '#dc2626',
                      strokeWidth: 1.5,
                      strokeDasharray: '6 4',
                    },
                  },
                ]}
                pointSize={5}
                enableSlices="x"
                sliceTooltip={({ slice }) => {
                  const date = String(slice.points[0]?.data.x ?? '');
                  return (
                    <Paper sx={{ p: 1.5, minWidth: 240 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Datum: {date}
                      </Typography>
                      {slice.points.map((p) => {
                        const row = rows.find(
                          (r) => r.domain === String(p.serieId) && r.stat_date === date,
                        );
                        const lb = row?.user_reported_spam_ratio_lower_bound;
                        const ub = row?.user_reported_spam_ratio_upper_bound;
                        return (
                          <Box key={p.id} sx={{ mt: 0.5 }}>
                            <Typography variant="body2" sx={{ color: p.serieColor }}>
                              {String(p.serieId)}: Spam-Rate{' '}
                              <strong>{(p.data.y as number).toFixed(3)} %</strong>
                            </Typography>
                            {lb !== null && lb !== undefined && ub !== null && ub !== undefined && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', ml: 0.5 }}
                              >
                                95 %-Konfidenz: {(lb * 100).toFixed(3)} % – {(ub * 100).toFixed(3)}{' '}
                                %
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Paper>
                  );
                }}
                legends={[
                  {
                    anchor: 'right',
                    direction: 'column',
                    translateX: 170,
                    itemWidth: 160,
                    itemHeight: 20,
                  },
                ]}
              />
            </Box>
          )}
        </Paper>

        {/* Domain reputation chart */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography variant="h6">Domain-Reputation pro Domain</Typography>
            <Tooltip title={TOOLTIPS.reputation} arrow>
              <HelpOutline sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
          </Box>
          {!loadingStats && (
            <Box sx={{ height: 320 }}>
              <ResponsiveLine
                data={reputationSeries}
                margin={{ top: 20, right: 180, bottom: 70, left: 90 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0.5, max: 4.5 }}
                axisBottom={{
                  tickRotation: -45,
                  legend: 'Datum',
                  legendPosition: 'middle',
                  legendOffset: 55,
                }}
                axisLeft={{
                  tickValues: [1, 2, 3, 4],
                  format: (v) => REPUTATION_LABEL[v as number] ?? '',
                  legend: 'Reputation',
                  legendOffset: -70,
                  legendPosition: 'middle',
                }}
                curve="stepAfter"
                pointSize={8}
                enableSlices="x"
                sliceTooltip={({ slice }) => (
                  <Paper sx={{ p: 1.5, minWidth: 200 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Datum: {String(slice.points[0]?.data.x ?? '')}
                    </Typography>
                    {slice.points.map((p) => (
                      <Typography key={p.id} variant="body2" sx={{ color: p.serieColor, mt: 0.5 }}>
                        {String(p.serieId)}:{' '}
                        <strong>{REPUTATION_LABEL[p.data.y as number] ?? '–'}</strong>
                      </Typography>
                    ))}
                  </Paper>
                )}
                legends={[
                  {
                    anchor: 'right',
                    direction: 'column',
                    translateX: 170,
                    itemWidth: 160,
                    itemHeight: 20,
                  },
                ]}
              />
            </Box>
          )}
        </Paper>

        {/* Raw table */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Rohdaten
          </Typography>
          <TableContainer sx={{ backgroundColor: '#ffffff', maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { label: '', tooltip: '' },
                    { label: 'Domain', tooltip: '' },
                    { label: 'Datum', tooltip: '' },
                    { label: 'Spam %', tooltip: TOOLTIPS.spam },
                    { label: 'SPF %', tooltip: TOOLTIPS.spf },
                    { label: 'DKIM %', tooltip: TOOLTIPS.dkim },
                    { label: 'DMARC %', tooltip: TOOLTIPS.dmarc },
                    { label: 'Domain-Rep.', tooltip: TOOLTIPS.reputation },
                    { label: 'Zustellfehler', tooltip: TOOLTIPS.deliveryErrors },
                  ].map((h, idx) => (
                    <TableCell
                      key={h.label || `empty-${idx}`}
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                    >
                      {h.tooltip ? <MetricLabel label={h.label} tooltip={h.tooltip} /> : h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => {
                  const rowKey = `${r.domain}-${r.stat_date}-${i}`;
                  const errors = Array.isArray(r.delivery_errors)
                    ? (r.delivery_errors as Array<Record<string, unknown>>)
                    : [];
                  const isExpanded = expandedRows.has(rowKey);
                  return (
                    <React.Fragment key={rowKey}>
                      <TableRow>
                        <TableCell sx={{ width: 40, p: 0.5 }}>
                          {errors.length > 0 && (
                            <IconButton size="small" onClick={() => toggleRow(rowKey)}>
                              {isExpanded ? (
                                <KeyboardArrowDown fontSize="small" />
                              ) : (
                                <KeyboardArrowRight fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>{r.domain}</TableCell>
                        <TableCell>{r.stat_date}</TableCell>
                        <TableCell sx={{ color: spamColor(r.user_reported_spam_ratio) }}>
                          {pct(r.user_reported_spam_ratio)}
                        </TableCell>
                        <TableCell sx={{ color: authColor(displaySpf(r)) }}>
                          {pct(displaySpf(r))}
                        </TableCell>
                        <TableCell sx={{ color: authColor(r.dkim_success_ratio) }}>
                          {pct(r.dkim_success_ratio)}
                        </TableCell>
                        <TableCell sx={{ color: authColor(r.dmarc_success_ratio) }}>
                          {pct(r.dmarc_success_ratio)}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: reputationColor(r.domain_reputation),
                            fontWeight: r.domain_reputation ? 600 : 400,
                          }}
                        >
                          {r.domain_reputation || '–'}
                        </TableCell>
                        <TableCell>
                          {errors.length > 0 ? (
                            <Chip
                              size="small"
                              label={`${errors.length} ${errors.length === 1 ? 'Fehler' : 'Fehler'}`}
                              color="error"
                              variant="outlined"
                            />
                          ) : (
                            '–'
                          )}
                        </TableCell>
                      </TableRow>
                      {errors.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ p: 0, borderBottom: isExpanded ? 1 : 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Zustellfehler von Gmail
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>errorClass</TableCell>
                                      <TableCell>errorType</TableCell>
                                      <TableCell>errorRatio</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {errors.map((e, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>{String(e.errorClass ?? '–')}</TableCell>
                                        <TableCell>{String(e.errorType ?? '–')}</TableCell>
                                        <TableCell>
                                          {e.errorRatio !== undefined
                                            ? pct(e.errorRatio as number)
                                            : '–'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && !loadingStats && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Keine Daten für den ausgewählten Zeitraum
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* IP reputation over time — multi-IP step chart */}
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography variant="h6">IP-Reputation im Zeitverlauf</Typography>
            <Tooltip
              title="Reputations-Verlauf pro Versand-IP. Wenn eine IP über mehrere Domains sendet, wird die schlechteste Einstufung des Tages angezeigt (das ist die, die die Domain-Reputation drückt)."
              arrow
            >
              <HelpOutline sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
          </Box>
          <Autocomplete
            multiple
            size="small"
            options={allIps}
            value={effectiveSelectedIps}
            onChange={(_, value) => setSelectedIps(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });
                return (
                  <Chip
                    label={option}
                    size="small"
                    {...tagProps}
                    key={tagProps.key}
                    sx={{ fontFamily: 'monospace' }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField {...params} label="IPs" placeholder="IP auswählen" size="small" />
            )}
            sx={{ mb: 2 }}
          />
          {ipReputationSeries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Keine IP-Daten im ausgewählten Zeitraum.
            </Typography>
          ) : (
            <Box sx={{ height: 340 }}>
              <ResponsiveLine
                data={ipReputationSeries}
                margin={{ top: 20, right: 200, bottom: 70, left: 100 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0.5, max: 4.5 }}
                axisBottom={{
                  tickRotation: -45,
                  legend: 'Datum',
                  legendPosition: 'middle',
                  legendOffset: 55,
                }}
                axisLeft={{
                  tickValues: [1, 2, 3, 4],
                  format: (v) => REPUTATION_LABEL[v as number] ?? '',
                  legend: 'Reputation',
                  legendOffset: -80,
                  legendPosition: 'middle',
                }}
                curve="stepAfter"
                pointSize={6}
                enableSlices="x"
                sliceTooltip={({ slice }) => (
                  <Paper sx={{ p: 1.5, minWidth: 220 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Datum: {String(slice.points[0]?.data.x ?? '')}
                    </Typography>
                    {slice.points.map((p) => (
                      <Typography
                        key={p.id}
                        variant="body2"
                        sx={{
                          color: p.serieColor,
                          mt: 0.5,
                          fontFamily: 'monospace',
                        }}
                      >
                        {String(p.serieId)}:{' '}
                        <strong>{REPUTATION_LABEL[p.data.y as number] ?? '–'}</strong>
                      </Typography>
                    ))}
                  </Paper>
                )}
                legends={[
                  {
                    anchor: 'right',
                    direction: 'column',
                    translateX: 190,
                    itemWidth: 170,
                    itemHeight: 20,
                    symbolShape: 'circle',
                  },
                ]}
              />
            </Box>
          )}
        </Paper>

        {/* IP Reputations per domain (latest day) */}
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography variant="h6">IP-Reputation pro Domain</Typography>
            <Tooltip title={TOOLTIPS.ipReputations} arrow>
              <HelpOutline sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Aufteilung der Versand-IPs in Reputations-Buckets am jeweils neuesten Datenpunkt. Eine
            IP mit LOW/BAD-Reputation drückt die Domain-Reputation mit runter.
          </Typography>
          {latestIpReputations.length === 0 && !loadingStats && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Keine IP-Daten für den ausgewählten Zeitraum.
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {latestIpReputations.map(({ domain, stat_date, buckets }) => {
              // Order buckets worst → best for visual impact
              const order = ['BAD', 'LOW', 'MEDIUM', 'HIGH'] as const;
              const sorted = order
                .map((rep) => buckets.find((b) => b.reputation === rep))
                .filter((b): b is IpReputationBucket => !!b);
              return (
                <Grid item xs={12} md={6} key={domain}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {domain}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Stand: {stat_date}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1.5,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        {sorted.map((b) => {
                          const count = b.ipCount ? Number(b.ipCount) : 0;
                          const ips = b.sampleIps ?? [];
                          if (count === 0 && ips.length === 0) return null;
                          return (
                            <Box
                              key={b.reputation}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                                p: 1,
                                borderLeft: 3,
                                borderColor: reputationColor(b.reputation),
                                bgcolor: '#fafafa',
                              }}
                            >
                              <Chip
                                size="small"
                                label={b.reputation}
                                sx={{
                                  bgcolor: reputationColor(b.reputation),
                                  color: 'white',
                                  fontWeight: 600,
                                  minWidth: 80,
                                }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2">
                                  {count} {count === 1 ? 'IP' : 'IPs'}
                                </Typography>
                                {ips.length > 0 && (
                                  <Box sx={{ mt: 0.5 }}>
                                    {ips.map((ip) => {
                                      const info = classifyIp(ip);
                                      return (
                                        <Box
                                          key={ip}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontFamily: 'monospace',
                                              color: 'text.secondary',
                                            }}
                                          >
                                            {ip}
                                          </Typography>
                                          {info && (
                                            <Typography
                                              variant="caption"
                                              sx={{ color: 'text.secondary' }}
                                            >
                                              — {info.provider} · <em>{info.pool}</em>
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Section 2b — Delivery-error breakdown (V2 replacement for V1's delivery_errors) */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
          Ablehnungsgründe (Delivery-Errors)
        </Typography>
        <PostmasterV2DeliveryErrorsTab selectedDomains={selectedDomains} />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Section 3 — Per-Campaign FBL spam rate (V2 FEEDBACK_LOOP_* drill-down) */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
          FBL pro Kampagne
        </Typography>
        <PostmasterV2FblTab selectedDomains={selectedDomains} />
      </Box>
    </Box>
  );
};

export default PostmasterDashboard;
